import React, { useState } from 'react';
import axios from 'axios';
import IntakeForm from './IntakeForm';
import FramingForm from './FramingForm';
import IdeasForm from './IdeasForm';
import JourneyReview from './JourneyReview';

const JourneyDetail = ({ configId, token, onBack }) => {
  const [journeyId, setJourneyId] = useState(null);
  const [cohortName, setCohortName] = useState('');
  const [currentStep, setCurrentStep] = useState('activation'); // activation, intake, framing, ideas, review
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Activate journey from config
  const handleActivateJourney = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await axios.post(
        `http://localhost:5000/api/journeys/${configId}/activate`,
        { cohort_name: cohortName.trim() || null },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setJourneyId(response.data.journey.id);
      setCurrentStep('intake');
      setSuccessMessage(
        `Journey activated! Now fill out the intake form below.`
      );
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        'Failed to activate journey. Please try again.';
      setError(errorMsg);
      console.error('Journey activation error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle successful intake submission
  const handleIntakeSuccess = (intakeData) => {
    setSuccessMessage('Intake submitted successfully! Now frame the problem.');
    setCurrentStep('framing');
  };

  // Handle successful framing submission
  const handleFramingSuccess = (framingData) => {
    setSuccessMessage('Framing submitted successfully! Ready for ideation.');
    setCurrentStep('ideas');
  };

  // Handle journey completion
  const handleJourneyComplete = (ideasList) => {
    setCurrentStep('review');
  };

  // Show appropriate form based on current step
  if (currentStep === 'activation' || !journeyId) {
    return (
      <div className="form-container">
        <div className="form-card">
          <h2>Activate Innovation Journey</h2>
          <p className="form-subtitle">Step 0: Create journey instance from config</p>

          {error && <div className="error-banner">{error}</div>}
          {successMessage && (
            <div className="success-banner">{successMessage}</div>
          )}

          <form onSubmit={handleActivateJourney}>
            <div className="form-group">
              <label htmlFor="cohort_name">Cohort Name (optional)</label>
              <input
                type="text"
                id="cohort_name"
                value={cohortName}
                onChange={(e) => setCohortName(e.target.value)}
                placeholder="e.g., Q3 2026 Mobile Redesign"
                maxLength="100"
              />
              <div className="character-counter">
                {cohortName.length}/100
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onBack}
                disabled={loading}
              >
                Back
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Activating...' : 'Activate Journey'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Show intake form after activation
  if (currentStep === 'intake') {
    return (
      <div>
        <IntakeForm
          journeyId={journeyId}
          token={token}
          onSuccess={handleIntakeSuccess}
          onCancel={onBack}
        />
      </div>
    );
  }

  // Show framing form after intake
  if (currentStep === 'framing') {
    return (
      <div>
        <FramingForm
          journeyId={journeyId}
          token={token}
          onSuccess={handleFramingSuccess}
          onCancel={() => setCurrentStep('intake')}
        />
      </div>
    );
  }

  // Show ideas form after framing
  if (currentStep === 'ideas') {
    return (
      <div>
        <IdeasForm
          journeyId={journeyId}
          token={token}
          onSuccess={handleJourneyComplete}
          onCancel={() => setCurrentStep('framing')}
        />
      </div>
    );
  }

  // Show review page after completion
  if (currentStep === 'review') {
    return (
      <div className="form-container">
        <JourneyReview
          configId={configId}
          journeyId={journeyId}
          token={token}
          onConfirm={onBack}
        />
      </div>
    );
  }
};

export default JourneyDetail;
